import { FluzElementsError } from "./errors.js";
import { resolveFrameHostOrigin } from "./frameHostOrigin.js";
import { mountFrame } from "./frameMount.js";
import type { FrameMount } from "./frameMount.js";
import type { StyleAllowlist } from "./style.js";

export interface FrameFieldConfig {
  field: string;
  // Whether reveal(field) may target this field on its own, independent of
  // reveal() revealing every mounted field together. Defaults to true.
  individualReveal?: boolean;
}

export type FrameField = string | FrameFieldConfig;

export interface FrameFieldViewerOptions {
  clientToken: string;
  loadToken: string;
  frameHostOrigin?: string;
  fields?: FrameField[];
  mountId?: string;
  style?: StyleAllowlist;
  mountTimeoutMs?: number;
  // How long reveal() waits for the frame(s) to report the value is visible
  // before rejecting with REVEAL_TIMEOUT. Defaults to 15000.
  revealTimeoutMs?: number;
}

export interface FrameFieldViewer {
  mount(target: HTMLElement): Promise<void>;
  // Resolves once every targeted field has rendered its revealed value;
  // rejects with a FluzElementsError if any field fails to reveal or the
  // frame doesn't report back within revealTimeoutMs.
  reveal(field?: string): Promise<void>;
  setMask(field: string, masked: boolean, options?: { hidden?: boolean }): void;
  onError(handler: (error: FluzElementsError) => void): () => void;
  onMount(handler: () => void): () => void;
  destroy(): void;
}

const DEFAULT_REVEAL_TIMEOUT_MS = 15000;

// The frame's reveal-result payload, echoing back the requestId reveal() sent.
interface RevealResult {
  requestId: string;
  ok: boolean;
  code?: string;
  retryAfterSeconds?: number | null;
}

function revealFailure(field: string, result: RevealResult): FluzElementsError {
  if (result.code === "rate_limited") {
    return new FluzElementsError(
      "RATE_LIMITED",
      typeof result.retryAfterSeconds === "number"
        ? `Rate limit exceeded. Retry after ${result.retryAfterSeconds}s.`
        : "Rate limit exceeded.",
    );
  }
  return new FluzElementsError("REVEAL_FAILED", `Failed to reveal "${field}": ${result.code ?? "reveal_failed"}`);
}

function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeFieldConfig(entry: FrameField): FrameFieldConfig {
  return typeof entry === "string" ? { field: entry, individualReveal: true } : { individualReveal: true, ...entry };
}

// Shared by createCardViewer and createAccountViewer -- both mount one iframe
// per field against the same frame-host protocol (handshake, reveal,
// set-mask), differing only in which field names are valid for their
// reveal purpose. `viewerName` is used only in error messages.
export function createFrameFieldViewer(
  viewerName: string,
  defaultFields: string[],
  options: FrameFieldViewerOptions,
): FrameFieldViewer {
  const fieldConfigs = (options.fields ?? defaultFields).map(normalizeFieldConfig);
  const allowedFields = new Set(defaultFields);
  const invalidField = fieldConfigs.find((config) => !allowedFields.has(config.field));
  if (invalidField) {
    throw new FluzElementsError("INVALID_FIELD", `Unknown field: "${invalidField.field}"`);
  }
  const fields = fieldConfigs.map((config) => config.field);
  const individualRevealByField = new Map(fieldConfigs.map((config) => [config.field, config.individualReveal !== false]));

  const frameHostOrigin = resolveFrameHostOrigin(options.frameHostOrigin);
  const baseMountId = options.mountId ?? generateId();

  const errorHandlers = new Set<(error: FluzElementsError) => void>();
  const mountHandlers = new Set<() => void>();
  const mounts = new Map<string, FrameMount>();
  const mountedFields = new Set<string>();
  let mounted = false;
  // True once every mountFrame() promise has settled successfully; unlike
  // mounts.size it can't be fooled by duplicate field names.
  let mountComplete = false;
  // Bumped by every mount() and destroy(), so a mount that finishes after a
  // destroy() or a newer mount() can tell it is stale and discard its frames.
  let generation = 0;
  let mountNotified = false;
  // Settles for one in-flight reveal request (keyed by requestId), either
  // with the frame's reveal-result or -- on destroy() -- a synthetic failure.
  const pendingReveals = new Map<string, (result: RevealResult) => void>();

  function emitError(error: FluzElementsError) {
    errorHandlers.forEach((handler) => handler(error));
  }

  function noteFieldMounted(field: string) {
    mountedFields.add(field);
    if (!mountNotified && mountedFields.size === fields.length) {
      mountNotified = true;
      mountHandlers.forEach((handler) => handler());
    }
  }

  return {
    async mount(target: HTMLElement) {
      if (mounted) {
        throw new FluzElementsError("MOUNT_FAILED", `${viewerName} is already mounted.`);
      }
      mounted = true;
      mountComplete = false;
      const myGeneration = ++generation;

      mountedFields.clear();
      mountNotified = false;

      const results = await Promise.allSettled(
        fields.map(async (field) => {
          const url = new URL(`/v1/frame/${field}`, frameHostOrigin);
          // loadToken (not clientToken) travels in the query string: it's
          // short-lived and scoped only to resolving the grant for this GET
          // (frame-host needs that pre-render, for the frame-ancestors CSP
          // header -- see frame-host routes/frame.ts). It can't authenticate
          // reveal/collect/payment-method, so a Referer/log/DOM leak of the
          // URL isn't a reveal-capable credential. The actual bearer
          // (clientToken) never touches the URL -- it's handed over below via
          // the handshake postMessage instead.
          url.searchParams.set("token", options.loadToken);

          const mount = await mountFrame({
            mountId: `${baseMountId}-${field}`,
            frameUrl: url.toString(),
            frameOrigin: frameHostOrigin,
            container: target,
            style: options.style,
            mountTimeoutMs: options.mountTimeoutMs,
            onError: emitError,
            handshakeExtra: { clientToken: options.clientToken },
            onMessage: (message) => {
              if (myGeneration !== generation) {
                return;
              }
              if (message.type === "mount") {
                noteFieldMounted(field);
                return;
              }
              if (message.type === "reveal-result") {
                const result = message.payload as RevealResult;
                if (result && typeof result.requestId === "string") {
                  pendingReveals.get(result.requestId)?.(result);
                }
              }
            },
          });

          if (myGeneration !== generation) {
            mount.destroy();
            return;
          }
          mounts.set(field, mount);
        }),
      );

      if (myGeneration !== generation) {
        throw new FluzElementsError("MOUNT_FAILED", `${viewerName} was destroyed or re-mounted before mounting finished.`);
      }

      const failure = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
      if (failure) {
        mounts.forEach((mount) => mount.destroy());
        mounts.clear();
        mounted = false;
        throw failure.reason instanceof FluzElementsError
          ? failure.reason
          : new FluzElementsError("MOUNT_FAILED", "Failed to mount one or more fields.");
      }
      mountComplete = true;
    },

    async reveal(field?: string) {
      if (!mounted) {
        throw new FluzElementsError("MOUNT_FAILED", `${viewerName} is not mounted.`);
      }

      let targets: Array<[string, FrameMount]>;
      if (field === undefined) {
        if (!mountComplete) {
          throw new FluzElementsError("MOUNT_FAILED", `${viewerName} is still mounting.`);
        }
        targets = [...mounts.entries()];
      } else {
        const mount = mounts.get(field);
        if (!mount) {
          throw new FluzElementsError("MOUNT_FAILED", `Unknown field: ${field}`);
        }
        if (!individualRevealByField.get(field)) {
          throw new FluzElementsError("INDIVIDUAL_REVEAL_DISABLED", `Field "${field}" is not enabled for individual reveal.`);
        }
        targets = [[field, mount]];
      }

      const timeoutMs = options.revealTimeoutMs ?? DEFAULT_REVEAL_TIMEOUT_MS;
      const outcomes = await Promise.all(
        targets.map(([targetField, mount]) => {
          const requestId = generateId();
          return new Promise<FluzElementsError | null>((resolve) => {
            const timeoutId = setTimeout(
              () => settle(new FluzElementsError("REVEAL_TIMEOUT", `Timed out revealing "${targetField}".`)),
              timeoutMs,
            );
            function settle(error: FluzElementsError | null) {
              clearTimeout(timeoutId);
              pendingReveals.delete(requestId);
              resolve(error);
            }
            pendingReveals.set(requestId, (result) => settle(result.ok ? null : revealFailure(targetField, result)));
            mount.send("reveal", { trigger: "button", requestId });
          });
        }),
      );

      // Every field has settled by now, so a resolved reveal() means all of
      // them are visible; otherwise surface the first failure.
      const failure = outcomes.find((outcome): outcome is FluzElementsError => outcome !== null);
      if (failure) {
        throw failure;
      }
    },

    setMask(field: string, masked: boolean, maskOptions?: { hidden?: boolean }) {
      const mount = mounts.get(field);
      if (!mount) {
        throw new FluzElementsError("MOUNT_FAILED", `Unknown field: ${field}`);
      }
      mount.send("set-mask", { masked, hidden: maskOptions?.hidden ?? false });
    },

    onError(handler: (error: FluzElementsError) => void) {
      errorHandlers.add(handler);
      return () => errorHandlers.delete(handler);
    },

    onMount(handler: () => void) {
      mountHandlers.add(handler);
      return () => mountHandlers.delete(handler);
    },

    destroy() {
      generation++;
      pendingReveals.forEach((settle, requestId) =>
        settle({ requestId, ok: false, code: "destroyed" }),
      );
      mounts.forEach((mount) => mount.destroy());
      mounts.clear();
      mounted = false;
      mountComplete = false;
      mountedFields.clear();
      mountNotified = false;
    },
  };
}
